import React, { useEffect, useState, useCallback } from 'react';
import { deleteGroup, getAvailableGroups } from '../../api/settings';
import {
  Box, Button,
  FormControlLabel, Snackbar, Switch,
  Table,
  TableBody,
  TableCell, TableContainer,
  TableHead,
  TableRow,
  TableSortLabel, TextField,
  Tooltip
} from '@mui/material';
import styles from '../users/components/UsersTableComponent/UserTableComponent.module.scss';
import { visuallyHidden } from '@mui/utils';
import { getComparator, stableSort } from '../../components/Table/data';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import RemoveRedEyeTwoToneIcon from '@mui/icons-material/RemoveRedEyeTwoTone';
import { Order } from '../../components/Table/types';
import DoDisturbOnTwoToneIcon from '@mui/icons-material/DoDisturbOnTwoTone';
import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import HorizontalRuleTwoToneIcon from '@mui/icons-material/HorizontalRuleTwoTone';
import NewCompetitionsDialog from './NewCompetitionsDialog';
import EmojiEventsTwoToneIcon from '@mui/icons-material/EmojiEventsTwoTone';
import ManageCompetitionRewardsDialog from './ManageCompetitionRewardsDialog';
import MuiAlert, { AlertColor } from '@mui/material/Alert';
import { IntersectionLoading } from '../../components/IntersectionLoading';

const columns = [
  { id: 'name', label: 'Name' },
  { id: 'description', label: 'Description' },
  { id: 'image', label: 'Image' },
  { id: 'leagues', label: 'Leagues' },
  { id: 'isPaid', label: 'Is paid' },
  { id: 'isDeleted', label: 'Is deleted' },
  { id: 'level', label: 'Level' },
  { id: 'price', label: 'Price' },
  { id: 'createdBy', label: 'Owner' }
];

const Competitions = () => {
  const [competitionsList, setCompetitionsList] = useState<any[]>([]);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [loading, setLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [filterByDeleted, setFilterByDeleted] = useState(false);
  const [filterByPaid, setFilterByPaid] = useState(false);
  const [searchQueryByName, setSearchQueryByName] = useState('');
  const [isOpenNewCompetitionDialog, setIsOpenNewCompetitionDialog] = useState(false);
  const [isOpenRewardsManagementDialog, setIsOpenRewardsManagementDialog] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState<any>(null);
  const [openSnackbar, setOpenSnackbar] = useState<{isOpen: boolean, alertType: AlertColor, message: string}>({
    isOpen: false,
    alertType: 'success',
    message: ''
  });
  
  useEffect(() => {
    (async () => {
      await handleGetGroupsByFilter();
    })();
  }, [filterByDeleted, filterByPaid]);
  
  const handleRequestSort = (event: React.MouseEvent<unknown>, property: any) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  
  const handleGetGroupsByFilter = async () => {
    const filterConfig = {
      type: 'PUBLIC',
      isDeleted: filterByDeleted,
      take: 20,
      ...(filterByPaid && { isPaid: filterByPaid }),
      ...(searchQueryByName.length && { name: searchQueryByName }),
    }
    const _competitionsList = await getAvailableGroups(filterConfig);
    if (Array.isArray(_competitionsList)) {
      setHasNextPage(_competitionsList?.length >= 20);
      setCompetitionsList(_competitionsList as any)
    } else {
      setCompetitionsList([]);
    }
  };
  
  const handleTooltipPreview = (valueType: string, value: any) => {
    if (!valueType || !value) {
      return <span />;
    }

    switch (valueType) {
      case 'isDeleted': {
        return <span>{value ? 'Deleted' : 'Undeleted'}</span>
      }
      case 'isPaid': {
        return <span>{value ? 'Paid' : 'Unpaid'}</span>
      }
      case 'level': {
        return <span>{value?.name || ''}</span>
      }
      case 'leagues': {
        return (
          <div className="max-w-[400px]">
            {value?.map((item: any, index: number) =>
              <span key={index.toString()}>{item.name}{value.length !== index + 1 && <span>, </span>}</span>
            )}
          </div>
        )
      }
      default: return <span>{typeof value === 'string' ? value : <DoDisturbOnTwoToneIcon color="warning"/>}</span>;
    }
  };
  
  const handleRowItem = (valueType: string, value: any) => {
    if (!valueType || !value) {
      return <span />;
    }

    switch (valueType) {
      case 'isDeleted':
        return <span />;
      case 'isPaid': {
        return value ? <CheckCircleTwoToneIcon color="success"/> : <DoDisturbOnTwoToneIcon color="error"/>
      }
      case 'description': {
        return <span className="max-w-[200px]">{value}</span>
      }
      case 'level': {
        return <span>{value?.name}</span>
      }
      case 'image': {
        return <img src={value?.url} alt={value?.url} className="w-[40px] h-auto rounded-md" />
      }
      case 'leagues': {
        return (
          <div className="max-w-[200px]">
            {value?.map((item: any, index: number) =>
              <span className="block" key={index.toString()}>{item.name}{value.length !== index + 1 && <span>, </span>}</span>
            )}
          </div>
        )
      }
      default: return <span>{typeof value === 'string' ? value : <HorizontalRuleTwoToneIcon color="disabled" />}</span>;
    }
  };

  const handleDeleteGroup = async (group: any) => {
    try {
      await deleteGroup(group.id)
      const updatedCompetitionsList = competitionsList.filter((item: any) => item.id !== group.id);
      setCompetitionsList(updatedCompetitionsList);
      setOpenSnackbar({ isOpen: true, message: 'Group has been successfully deleted', alertType: 'success' });
    } catch (e) {
      setOpenSnackbar({
        isOpen: true,
        message: 'Something went wrong, unable to delete the group, please try again later',
        alertType: 'error'
      });
    }
  };
  
  const handleSearchGroupsByName = async () => {
    await handleGetGroupsByFilter();
  };

  const onLoadMore = async () => {
    const filterConfig = {
      type: 'PUBLIC',
      isDeleted: filterByDeleted,
      cursor: competitionsList[competitionsList?.length - 1]?.id,
      take: 20,
      ...(filterByPaid && { isPaid: filterByPaid }),
      ...(searchQueryByName.length && { name: searchQueryByName }),
    }
    const _competitionsList = await getAvailableGroups(filterConfig);
    
    if (Array.isArray(_competitionsList)) {
      setHasNextPage(_competitionsList?.length >= 20);
      setCompetitionsList(prev => {
        return prev.concat(_competitionsList);
      });
    }
  };
  
  return (
    <div>
      <div className="mt-5 mb-5 flex items-center">
        <FormControlLabel
          labelPlacement="start"
          className="block w-max text-xl"
          control={
            <Switch
              defaultChecked={false}
              checked={filterByPaid}
              onChange={(event) => setFilterByPaid(event.target.checked)}
            />
          }
          label="Only paid competitions"
        />
        <FormControlLabel
          labelPlacement="start"
          className="block w-max text-xl !ml-10"
          control={
            <Switch
              defaultChecked={false}
              checked={filterByDeleted}
              onChange={(event) => setFilterByDeleted(event.target.checked)}
            />
          }
          label="Only deleted competitions"
        />
        <div className="ml-14 flex items-center h-max">
          <TextField
            color="info"
            label="Search competitions by name"
            value={searchQueryByName}
            onChange={(event) => setSearchQueryByName(event.target.value)}
            className="w-[300px]"
          />
          <Button
            className="!ml-4 h-max"
            variant="contained"
            onClick={handleSearchGroupsByName}
          >
            Search
          </Button>
        </div>
      </div>
      {competitionsList.length > 0 && (
        <TableContainer sx={{ maxHeight: "60vh", width: '100%', position: 'relative', marginBottom: '20px' }} id="CompetitionsTable">
          <Table stickyHeader aria-labelledby="tableTitle" size="small" className={styles.table}>
            <TableHead>
              <TableRow>
                {columns?.map((item: any, index: number) => (
                  <TableCell
                    key={`competitionsList-row-header-${index}`}
                    style={{ minWidth: 50 }}
                    sortDirection={orderBy === item.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === item.id}
                      direction={orderBy === item.id ? order : 'asc'}
                      onClick={(event) => handleRequestSort(event, item.id)}
                      className="capitalize font-bold"
                    >
                      {item.label}
                      {orderBy === item.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
                {competitionsList.length > 0 && (
                  <TableCell key="action" align="center">
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {stableSort(competitionsList as any, getComparator(order, orderBy))?.map((row: any, index: number) => {
                return (
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={`competitionsItem-row-${row?.id}-${index}`}
                    className={styles.tableRow}
                    onClick={() => {
                      setSelectedCompetition(row);
                      setIsOpenNewCompetitionDialog(true);
                    }}
                  >
                    {columns.map((column, indexCell) => {
                      return (
                        <TableCell  key={`competitionsItem-TableCell-${row?.id}-${index}-${indexCell}`} className={styles.rowItemContainer}>
                          <Tooltip title={handleTooltipPreview(column?.id, row?.[column?.id])} placement="top">
                            {handleRowItem(column?.id, row?.[column?.id])}
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                    <TableCell key="action" align="center">
                      <Tooltip title="Manage competition places and rewards" placement="top">
                        <EmojiEventsTwoToneIcon
                          color="warning"
                          onClick={(event) => {
                            event.stopPropagation();
                            setIsOpenRewardsManagementDialog(true);
                            setSelectedCompetition(row);
                          }}
                          className="!mr-1"
                        />
                      </Tooltip>
                      {!row.isDeleted ? (
                        <Tooltip title={`Delete group "${row['name']}"`} placement="top">
                          <DeleteTwoToneIcon color="error" onClick={(event) => {
                            event.stopPropagation();
                            handleDeleteGroup(row);
                          }} />
                        </Tooltip>
                      ) : (
                        <Tooltip title={`View "${row['name']}"`} placement="top">
                          <RemoveRedEyeTwoToneIcon color="info" />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {hasNextPage && (
            <IntersectionLoading
              className="!mt-5"
              options={{
                root: document.querySelector("#CompetitionsTable"),
              }}
              callback={onLoadMore}
            />
          )}
        </TableContainer>
      )}
      {competitionsList?.length === 0 && (
        <span className="text-xl font-bold">Немає даних для відображення...</span>
      )}
      <div className="mt-10">
        <Button variant="contained" onClick={() => setIsOpenNewCompetitionDialog(true)}>
          Create new competition
        </Button>
      </div>
      <NewCompetitionsDialog
        isOpen={isOpenNewCompetitionDialog}
        handleCloseNewGroupDialog={(e) => {
          setIsOpenNewCompetitionDialog(false)
          setSelectedCompetition(null)
        }}
        selectedCompetition={selectedCompetition}
        handleOnUpdate={handleGetGroupsByFilter}
      />
      <ManageCompetitionRewardsDialog
        isOpen={isOpenRewardsManagementDialog}
        close={() => setIsOpenRewardsManagementDialog(false)}
        competitionId={selectedCompetition?.id}
      />
      <Snackbar
        open={openSnackbar.isOpen}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar({...openSnackbar, isOpen: false})}
      >
        <MuiAlert
          onClose={() => setOpenSnackbar({...openSnackbar, isOpen: false})}
          severity={openSnackbar.alertType}
          sx={{ width: '100%' }}
        >
          {openSnackbar.message}
        </MuiAlert>
      </Snackbar>
    </div>
  )
};

export default Competitions;